import React, { useState } from "react";
import { CalendarIcon, SparklesIcon, PlusIcon, TrashIcon, CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components";
import { useGeminiMutations } from "../../../../hooks/data/useProjectMutations";
import type { MilestonesPhaseProps } from "./milestonesPhase.types";
import type { ProjectTimeline, Milestone } from "@/features/Projects";
import { MilestonesSkeleton } from "./components";

// Default timeline structure when none exists
const getDefaultTimeline = (): ProjectTimeline => ({
  milestones: [],
  totalDuration: 0,
  criticalPath: [],
  startDate: new Date().toISOString(),
  endDate: new Date().toISOString(),
});

// Milestone edit form component
interface MilestoneEditFormProps {
  milestone: Milestone;
  index: number;
  onSave: (milestone: Milestone) => void;
  onCancel: () => void;
}

function MilestoneEditForm({ milestone, index, onSave, onCancel }: MilestoneEditFormProps) {
  const [editedMilestone, setEditedMilestone] = useState<Milestone>(milestone);

  const handleInputChange = (field: keyof Milestone, value: any) => {
    setEditedMilestone(prev => ({ ...prev, [field]: value }));
  };

  const handleDeliverablesChange = (value: string) => {
    const deliverables = value.split(',').map(d => d.trim()).filter(d => d.length > 0);
    setEditedMilestone(prev => ({ ...prev, deliverables }));
  };

  const handleSave = () => {
    // Recalculate dates based on duration
    const startDate = new Date();
    const endDate = new Date(startDate.getTime() + editedMilestone.estimatedDuration * 24 * 60 * 60 * 1000);
    
    const updatedMilestone = {
      ...editedMilestone,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
    
    onSave(updatedMilestone);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2 mb-4">
        <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full">
          Editando Marco {index + 1}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Título do Marco
          </label>
          <input
            type="text"
            value={editedMilestone.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            placeholder="Nome do marco..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Duração (dias)
          </label>
          <input
            type="number"
            value={editedMilestone.estimatedDuration}
            onChange={(e) => handleInputChange('estimatedDuration', parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            min="1"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Fase
          </label>
          <select
            value={editedMilestone.phase}
            onChange={(e) => handleInputChange('phase', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="description">Descrição</option>
            <option value="objectives">Objetivos</option>
            <option value="technical_solution">Solução Técnica</option>
            <option value="milestones">Marcos</option>
            <option value="stories">User Stories</option>
            <option value="schema">Schema Técnico</option>
            <option value="resources">Recursos</option>
            <option value="proposal">Proposta</option>
            <option value="execution">Execução</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Prioridade
          </label>
          <select
            value={editedMilestone.priority}
            onChange={(e) => handleInputChange('priority', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="low">Baixa</option>
            <option value="medium">Média</option>
            <option value="high">Alta</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Descrição
        </label>
        <textarea
          value={editedMilestone.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          rows={3}
          placeholder="Descreva o que deve ser entregue neste marco..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Entregáveis (separados por vírgula)
        </label>
        <input
          type="text"
          value={editedMilestone.deliverables.join(', ')}
          onChange={(e) => handleDeliverablesChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          placeholder="Documentação, Protótipo, Código..."
        />
      </div>

      <div className="flex justify-end space-x-2 pt-4 border-t">
        <Button
          onClick={onCancel}
          variant="secondary"
          size="sm"
          leftIcon={<XMarkIcon className="h-4 w-4" />}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          size="sm"
          leftIcon={<CheckIcon className="h-4 w-4" />}
        >
          Salvar Alterações
        </Button>
      </div>
    </div>
  );
}

export default function MilestonesPhase({ 
  project, 
  onSave, 
  onNext 
}: MilestonesPhaseProps) {
  const [timeline, setTimeline] = useState<ProjectTimeline>(
    project.timeline || getDefaultTimeline()
  );
  const [isEditing, setIsEditing] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<string | null>(null);
  
  const { generateMilestones, isGeneratingMilestones } = useGeminiMutations();

  // Handle generating milestones with AI
  const handleGenerateFromAI = async () => {
    try {
      const response = await generateMilestones({
        projectId: project.id,
        projectDescription: project.description,
        objectives: project.objectives || [],
        technicalSolution: project.technicalSolution,
        preferredStartDate: new Date().toISOString(),
      });
      
      const newTimeline = response.timeline;
      setTimeline(newTimeline);
      
      // Auto-save the generated timeline
      await onSave(newTimeline);
    } catch (error) {
      console.error("❌ Erro ao gerar timeline:", error);
    }
  };

  // Handle saving timeline
  const handleSaveTimeline = async (updatedTimeline: ProjectTimeline) => {
    setTimeline(updatedTimeline);
    await onSave(updatedTimeline);
  };

  // Handle creating timeline manually
  const handleCreateManually = () => {
    // Create a basic timeline structure to start with
    const basicTimeline: ProjectTimeline = {
      milestones: [],
      totalDuration: 0,
      criticalPath: [],
      startDate: new Date().toISOString(),
      endDate: new Date().toISOString(),
    };
    
    setTimeline(basicTimeline);
    setIsEditing(true);
  };

  // Handle adding a new milestone
  const handleAddMilestone = () => {
    const newMilestone = {
      id: `milestone-${Date.now()}`,
      title: "Novo Marco",
      description: "Descrição do marco",
      estimatedDuration: 7,
      dependencies: [],
      phase: "milestones" as const,
      deliverables: ["Entregável principal"],
      startDate: new Date().toISOString(),
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      priority: "medium" as const,
      status: "not_started" as const,
    };

    const updatedTimeline = {
      ...timeline,
      milestones: [...timeline.milestones, newMilestone],
      totalDuration: timeline.totalDuration + newMilestone.estimatedDuration,
    };

    setTimeline(updatedTimeline);
    handleSaveTimeline(updatedTimeline);
  };

  // Handle deleting a milestone
  const handleDeleteMilestone = (milestoneId: string) => {
    const updatedMilestones = timeline.milestones.filter(m => m.id !== milestoneId);
    const deletedMilestone = timeline.milestones.find(m => m.id === milestoneId);
    
    const updatedTimeline = {
      ...timeline,
      milestones: updatedMilestones,
      totalDuration: timeline.totalDuration - (deletedMilestone?.estimatedDuration || 0),
      // Remove from critical path if it was there
      criticalPath: timeline.criticalPath.filter(id => id !== milestoneId)
    };

    setTimeline(updatedTimeline);
    handleSaveTimeline(updatedTimeline);
  };

  // Handle editing a milestone
  const handleEditMilestone = (milestoneId: string) => {
    setEditingMilestone(milestoneId);
  };

  // Handle updating a milestone
  const handleUpdateMilestone = (updatedMilestone: Milestone) => {
    const oldMilestone = timeline.milestones.find(m => m.id === updatedMilestone.id);
    const updatedMilestones = timeline.milestones.map(m => 
      m.id === updatedMilestone.id ? updatedMilestone : m
    );
    
    const durationDiff = updatedMilestone.estimatedDuration - (oldMilestone?.estimatedDuration || 0);
    
    const updatedTimeline = {
      ...timeline,
      milestones: updatedMilestones,
      totalDuration: timeline.totalDuration + durationDiff
    };

    setTimeline(updatedTimeline);
    setEditingMilestone(null);
    handleSaveTimeline(updatedTimeline);
  };

  // Handle canceling milestone edit
  const handleCancelEditMilestone = () => {
    setEditingMilestone(null);
  };

  // Handle continuing to next phase
  const handleNext = async () => {
    if (timeline.milestones.length > 0) {
      await onSave(timeline);
    }
    onNext();
  };

  const hasTimeline = timeline.milestones.length > 0;
  const hasTimelineOrEditing = hasTimeline || isEditing;
  const totalDays = timeline.totalDuration;
  const totalMilestones = timeline.milestones.length;

  if (isGeneratingMilestones) {
    return <MilestonesSkeleton />;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <CalendarIcon className="h-5 w-5 text-orange-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {hasTimeline ? "Timeline do Projeto" : "Criar Timeline"}
              </h2>
              <p className="text-sm text-gray-600">
                {hasTimeline 
                  ? `${totalMilestones} marcos • ${totalDays} dias • Caminho crítico definido`
                  : "Gere automaticamente os marcos e cronograma do projeto"
                }
              </p>
            </div>
          </div>
          
          {hasTimeline && (
            <Button
              onClick={() => setIsEditing(!isEditing)}
              variant="secondary"
              size="sm"
            >
              {isEditing ? "Visualizar" : "Editar Timeline"}
            </Button>
          )}
        </div>

        {/* Timeline Summary when collapsed */}
        {hasTimeline && !isEditing && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-orange-50 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <CalendarIcon className="h-4 w-4 text-orange-600" />
                <span className="text-sm font-medium text-orange-900">Duração Total</span>
              </div>
              <p className="text-lg font-semibold text-orange-900 mt-1">
                {totalDays} dias
              </p>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <span className="w-4 h-4 bg-blue-600 rounded-full"></span>
                <span className="text-sm font-medium text-blue-900">Total de Marcos</span>
              </div>
              <p className="text-lg font-semibold text-blue-900 mt-1">
                {totalMilestones} marcos
              </p>
            </div>
            
            <div className="bg-green-50 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <span className="w-4 h-4 bg-green-600 rounded-full"></span>
                <span className="text-sm font-medium text-green-900">Caminho Crítico</span>
              </div>
              <p className="text-lg font-semibold text-green-900 mt-1">
                {timeline.criticalPath.length} marcos
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Generate AI Timeline Option */}
      {!hasTimeline && (
        <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border border-orange-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 p-2 bg-orange-100 rounded-lg">
              <SparklesIcon className="h-5 w-5 text-orange-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-md font-medium text-gray-900 mb-1">
                Gerar Timeline com IA
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                Com base na descrição do projeto, objetivos e solução técnica, 
                a IA criará automaticamente um cronograma detalhado com marcos realistas.
              </p>
              <div className="flex space-x-2">
                <Button
                  onClick={handleGenerateFromAI}
                  disabled={isGeneratingMilestones}
                  leftIcon={<SparklesIcon className="h-4 w-4" />}
                  size="sm"
                >
                  {isGeneratingMilestones ? "Gerando..." : "Gerar Timeline"}
                </Button>
                <Button
                  onClick={handleCreateManually}
                  variant="secondary"
                  size="sm"
                >
                  Criar Manualmente
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Timeline Visualization */}
      {hasTimelineOrEditing && isEditing && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-md font-medium text-gray-900">
              Editar Timeline
            </h3>
            <div className="flex space-x-2">
              <Button
                onClick={handleGenerateFromAI}
                disabled={isGeneratingMilestones}
                variant="secondary"
                size="sm"
                leftIcon={<SparklesIcon className="h-4 w-4" />}
              >
                Regerar com IA
              </Button>
              <Button
                onClick={handleAddMilestone}
                variant="secondary"
                size="sm"
                leftIcon={<PlusIcon className="h-4 w-4" />}
              >
                Adicionar Marco
              </Button>
            </div>
          </div>
          
          {/* Milestones List */}
          <div className="space-y-3">
            {timeline.milestones.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                <CalendarIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                <h3 className="text-sm font-medium text-gray-900 mb-2">
                  Nenhum marco criado ainda
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Adicione marcos para organizar o cronograma do seu projeto
                </p>
                <Button
                  onClick={handleAddMilestone}
                  leftIcon={<PlusIcon className="h-4 w-4" />}
                  size="sm"
                >
                  Criar Primeiro Marco
                </Button>
              </div>
            ) : (
              timeline.milestones.map((milestone, index) => (
              <div
                key={milestone.id}
                className="bg-gray-50 rounded-lg p-4 border-l-4 border-orange-500"
              >
                {editingMilestone === milestone.id ? (
                  <MilestoneEditForm
                    milestone={milestone}
                    index={index}
                    onSave={handleUpdateMilestone}
                    onCancel={handleCancelEditMilestone}
                  />
                ) : (
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full">
                        Marco {index + 1}
                      </span>
                      <span className="bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full">
                        {milestone.phase}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        milestone.priority === 'high' 
                          ? 'bg-red-100 text-red-800' 
                          : milestone.priority === 'medium'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {milestone.priority}
                      </span>
                    </div>
                    <h4 className="font-medium text-gray-900 mb-1">
                      {milestone.title}
                    </h4>
                    <p className="text-sm text-gray-600 mb-2">
                      {milestone.description}
                    </p>
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span>⏱️ {milestone.estimatedDuration} dias</span>
                      <span>📦 {milestone.deliverables.length} entregáveis</span>
                      {milestone.dependencies.length > 0 && (
                        <span>🔗 {milestone.dependencies.length} dependências</span>
                      )}
                    </div>
                    {milestone.deliverables.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500 mb-1">Entregáveis:</p>
                        <div className="flex flex-wrap gap-1">
                          {milestone.deliverables.slice(0, 3).map((deliverable, i) => (
                            <span
                              key={i}
                              className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
                            >
                              {deliverable}
                            </span>
                          ))}
                          {milestone.deliverables.length > 3 && (
                            <span className="text-xs text-gray-500">
                              +{milestone.deliverables.length - 3} mais
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => handleEditMilestone(milestone.id)}
                      variant="ghost"
                      size="sm"
                    >
                      Editar
                    </Button>
                    <Button
                      onClick={() => handleDeleteMilestone(milestone.id)}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      leftIcon={<TrashIcon className="h-4 w-4" />}
                    >
                      Remover
                    </Button>
                  </div>
                  </div>
                )}
              </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 sticky bottom-4 shadow-lg">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">
            {hasTimeline 
              ? "Timeline criada • Alterações salvas automaticamente"
              : "Crie uma timeline para continuar"
            }
          </span>
          
          <Button
            onClick={handleNext}
            disabled={!hasTimeline}
            className="px-6"
          >
            Continuar para Stories
          </Button>
        </div>
      </div>
    </div>
  );
}